import React, { useEffect, useState } from "react";
import { Box, Button, Container, Typography, styled } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const HeroBg = styled(Box)(({ theme }) => ({
  position: "relative",
  backgroundColor: "#0d1620",
  color: "#fff",
  paddingTop: theme.spacing(14),
  paddingBottom: theme.spacing(11),
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(11),
    paddingBottom: theme.spacing(8),
  },
}));

const Label = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#7ec1e2",
  marginBottom: 22,
});

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "3.4rem",
  fontWeight: 700,
  lineHeight: 1.12,
  letterSpacing: "-0.022em",
  color: "#fff",
  marginBottom: theme.spacing(3),
  maxWidth: 900,
  [theme.breakpoints.down("md")]: { fontSize: "2.5rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.95rem" },
}));

const Lead = styled(Typography)(({ theme }) => ({
  fontSize: "1.1rem",
  lineHeight: 1.65,
  color: "rgba(255, 255, 255, 0.72)",
  marginBottom: theme.spacing(4),
  maxWidth: 720,
  [theme.breakpoints.down("sm")]: { fontSize: "1rem" },
}));

const PrimaryButton = styled(Button)({
  padding: "12px 24px",
  borderRadius: 6,
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  backgroundColor: "#2f81ad",
  color: "#fff",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1d648c", boxShadow: "none" },
  transition: "background-color 0.2s ease",
});

const GhostButton = styled(Button)(({ theme }) => ({
  padding: "11px 22px",
  borderRadius: 6,
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  color: "#fff",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  backgroundColor: "transparent",
  marginLeft: theme.spacing(1.5),
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.06)",
    borderColor: "rgba(255, 255, 255, 0.35)",
  },
  transition: "all 0.2s ease",
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    marginTop: theme.spacing(1.5),
  },
}));

const BadgesBar = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(7),
  paddingTop: theme.spacing(4),
  borderTop: "1px solid rgba(255, 255, 255, 0.08)",
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.spacing(4),
  [theme.breakpoints.down("sm")]: { gap: theme.spacing(2.5) },
}));

const Badge = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
});

const BadgeValue = styled(Typography)({
  fontSize: "1.05rem",
  fontWeight: 700,
  color: "#fff",
  letterSpacing: "-0.01em",
  lineHeight: 1.2,
});

const BadgeLabel = styled(Typography)({
  fontSize: "0.76rem",
  fontWeight: 500,
  color: "rgba(255, 255, 255, 0.55)",
  letterSpacing: "0.02em",
  marginTop: 3,
});

const Divider = styled("span")({
  width: 1,
  height: 28,
  backgroundColor: "rgba(255, 255, 255, 0.1)",
});

const BADGES = [
  { value: "Est. 2019", label: "6+ years shipping" },
  { value: "25+", label: "Products delivered" },
  { value: "20", label: "Senior engineers" },
  { value: "Ontario · Lahore", label: "Two-office delivery" },
  { value: "100%", label: "IP transfer" },
];

interface HeroSectionProps {
  onStartProject?: () => void;
  onLearnMore?: () => void;
}

const EnhancedHeroSection: React.FC<HeroSectionProps> = ({
  onStartProject,
  onLearnMore,
}) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <HeroBg component="section" aria-labelledby="hero-heading">
      <Container maxWidth="lg">
        <Box
          sx={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 0.5s ease, transform 0.5s ease",
          }}
        >
          <Label>Smart IT Innovation</Label>
          <Title id="hero-heading" variant="h1">
            Senior software &amp; AI engineering partner for product-led teams.
          </Title>
          <Lead>
            We combine senior engineering with applied AI to design, build, and
            ship secure, scalable products — for startups, fintech, healthcare,
            and SaaS teams across North America.
          </Lead>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <PrimaryButton
              onClick={onStartProject}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
              disableElevation
            >
              Book a discovery call
            </PrimaryButton>
            <GhostButton onClick={onLearnMore}>See our case studies</GhostButton>
          </Box>

          <BadgesBar>
            {BADGES.map((badge, i) => (
              <React.Fragment key={badge.label}>
                <Badge>
                  <BadgeValue>{badge.value}</BadgeValue>
                  <BadgeLabel>{badge.label}</BadgeLabel>
                </Badge>
                {i < BADGES.length - 1 && (
                  <Divider sx={{ display: { xs: "none", md: "block" } }} />
                )}
              </React.Fragment>
            ))}
          </BadgesBar>
        </Box>
      </Container>
    </HeroBg>
  );
};

export default EnhancedHeroSection;
